<template>
  <div class="signup">
    <img class="logotype" src="@/assets/streamify-logotype-blue.svg" />
    <template v-if="done">
      <transition name="slide-fade" mode="out-in">
        <card-minimal class="signup-card type m-t-4" key="type" v-if="step === 0">
          <div>
            <h1>Choose account type</h1>
            <p>Changes the features available on the account.</p>
            <div class="account-types">
              <div :class="`account-type ${accountType === 'liveshopping' ? 'active' : ''}`" @click="accountType = 'liveshopping'">
                <i class="icon-shoppingbag" />
                <h2>Live Shopping</h2>
                <p>Sell your products live</p>
              </div>
              <div :class="`account-type ${accountType === 'ppv' ? 'active' : ''}`" @click="accountType = 'ppv'">
                <i class="icon-ticket-large" />
                <h2>Pay-per-view</h2>
                <p>Sell access to your broadcasts</p>
              </div>
            </div>
          </div>
          <div class="signup-card-footer d-f">
            <Button class="only-text blue no-side-padding add-hover" icon="chevron-left-24" @click="$router.push(`/login${redirect ? `?redirect=${redirect}` : ''}`)">Cancel</Button>
            <Button class="ml-a" @click="step = 1" :disabled="accountType === ''">Continue</Button>
          </div>
        </card-minimal>
        <card-minimal class="signup-card details m-t-4" key="details" v-else>
          <div>
            <h1>Account details</h1>
            <div class="account-form">
              <label class="normal" for="name">{{ accountType === 'liveshopping' ? 'Company name' : 'Account name' }}</label>
              <FormInput
                icon="company-24"
                id="name"
                ref="accountName"
                :placeholder="accountType === 'liveshopping' ? 'Company Inc.' : 'Account name'"
                :loading="accountData.accountName.state === 'loading'"
                :stateIcon="`${accountData.accountName.state === 'error' ? 'warning' : ''}${accountData.accountName.state === 'valid' ? 'check-circle-24' : ''}`"
                :stateIconColor="`${accountData.accountName.state === 'error' ? 'red' : ''} ${accountData.accountName.state === 'valid' ? 'green' : ''}`"
                :error="accountData.accountName.state === 'error' ? accountData.accountName.error : ''"
                :value="accountData.accountName.value"
                v-model="accountData.accountName.value"
                @validate="checkAccountName(false)"
              />
              <label class="normal">Your name <span>For the owner of the account</span></label>
              <FormInput
                icon="user-24"
                id="username"
                ref="name"
                placeholder="John Appleseed"
                :stateIcon="`${accountData.name.state === 'error' ? 'warning' : ''}${accountData.name.state === 'valid' ? 'check-circle-24' : ''}`"
                :stateIconColor="`${accountData.name.state === 'error' ? 'red' : ''} ${accountData.name.state === 'valid' ? 'green' : ''}`"
                :error="accountData.name.error"
                :value="accountData.name.value"
                v-model="accountData.name.value"
                @validate="validateName(false)"
              />
              <label class="normal">E-mail <span>For the owner of the account</span></label>
              <FormInput
                icon="e-mail-24"
                id="email"
                ref="email"
                placeholder="john@appleseed.net"
                :stateIcon="`${accountData.email.state === 'error' ? 'warning' : ''}${accountData.email.state === 'valid' ? 'check-circle-24' : ''}`"
                :stateIconColor="`${accountData.email.state === 'error' ? 'red' : ''} ${accountData.email.state === 'valid' ? 'green' : ''}`"
                :value="accountData.email.value"
                :error="accountData.email.error"
                v-model="accountData.email.value"
                @validate="validateEmail(false)"
              />
              <DidYouMean :email="accountData.email.value" @correctEmail="accountData.email.value = $event;" ref="didYouMean" />
              <label class="normal">Password <span>For the owner of the account</span></label>
              <FormInput
                icon="password-24"
                id="password"
                type="password"
                ref="password"
                placeholder="********"
                :stateIcon="`${accountData.password.state === 'error' ? 'warning' : ''}${accountData.password.state === 'valid' ? 'check-circle-24' : ''}`"
                :stateIconColor="`${accountData.password.state === 'error' ? 'red' : ''} ${accountData.password.state === 'valid' ? 'green' : ''}`"
                :error="accountData.password.error"
                :value="accountData.password.value"
                v-model="accountData.password.value"
                @validate="validatePassword(false)"
              />
              <template v-if="accountType === 'liveshopping'">
                <label class="normal">Phone number <span>For the owner of the account</span></label>
                <FormInput
                  icon="phone-smart-24"
                  id="phonenumber"
                  type="tel"
                  ref="phone"
                  placeholder="123456789"
                  :stateIcon="`${accountData.phone.state === 'error' ? 'warning' : ''}${accountData.phone.state === 'valid' ? 'check-circle-24' : ''}`"
                  :stateIconColor="`${accountData.phone.state === 'error' ? 'red' : ''} ${accountData.phone.state === 'valid' ? 'green' : ''}`"
                  :error="accountData.phone.error"
                  :value="accountData.phone.value"
                  v-model="accountData.phone.value"
                  @validate="validatePhone(false)"
                />
              </template>
              <template v-else>
                <FormDropdown
                  class="m-t-3"
                  icon="factory"
                  id="industry"
                  tabindex="0"
                  :error="accountData.industry.error"
                  :items="industry"
                  :placeholder="$t('login.choose_industry')"
                  @input="industry = $event.value"
                />
              </template>
              <FormCheckbox
                class="m-t-2"
                icon="check"
                :text="tacText"
                :selected="accountData.tac.value"
                @input="accountData.tac.value = !accountData.tac.value"
                @linkClick="accountData.tac.show = true"
              />
            </div>
          </div>
          <div class="signup-card-footer d-f">
            <Button class="only-text blue no-side-padding add-hover" icon="chevron-left-24" @click="step = 0">Back</Button>
            <Button class="ml-a" @click="signup" :loading="state === 'creating'" :disabled="state === 'creating'">{{ accountType === 'liveshopping' ? 'Start free 30-day trial' : 'Create account' }}</Button>
          </div>
        </card-minimal>
      </transition>
      <ul class="steps">
        <li :class="step === 0 ? 'active' : ''"></li>
        <li :class="step === 1 ? 'active' : ''"></li>
      </ul>
    </template>

    <portal to="modals">
      <Modal class="state" :show="accountData.tac.show" @close="accountData.tac.show = false">
        <div class="tac-content" v-html="accountData.tac.content"></div>
        <template v-slot:footer>
          <div class="d-f f-w">
            <Button @click="accountData.tac.value = true; accountData.tac.show = false;">Accept</Button>
            <Button @click="accountData.tac.show = false;" class="red outline ml-a">Cancel</Button>
          </div>
        </template>
      </Modal>
    </portal>

    <component :is="'script'">
      _linkedin_partner_id = "3972250";
      window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
      window._linkedin_data_partner_ids.push(_linkedin_partner_id);
    </component>
    <component :is="'script'">
      (function(l) {
        if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
        window.lintrk.q=[]}
        var s = document.getElementsByTagName("script")[0];
        var b = document.createElement("script");
        b.type = "text/javascript";b.async = true;
        b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
        s.parentNode.insertBefore(b, s);
      })(window.lintrk);
    </component>
  </div>
</template>

<script>

import { getCookie, getQueryParameterValue } from '@/utils';

import DidYouMean from '@/components/DidYouMean.vue';

export default {
  components: {
    DidYouMean,
  },
  computed: {
    /**
     * Return text and html for tac-button
     * @returns {String}
     */
    tacText() {
      return `${this.$t('login.tac_label_1')} <a href="#">${this.$t('login.tac_label_2')}</a>`;
    },
    /**
     * Return and set industry for account
     */
    industry: {
      /**
       * Get industry
       * @returns {Array}
       */
      get() {
        return [
          {
            string: this.$t('common.industries.e-commerce'),
            value: 1,
          },
          {
            string: this.$t('common.industries.entertainment'),
            value: 2,
          },
          {
            string: this.$t('common.industries.sports'),
            value: 3,
          },
          {
            string: this.$t('common.industries.educational'),
            value: 4,
          },
          {
            string: this.$t('common.industries.events'),
            value: 5,
          },
          {
            string: this.$t('common.industries.other'),
            value: 0,
          },
        ].map(industry => ({ ...industry, selected: industry.value === this.accountData.industry.value }));
      },
      /**
       * Set industry on account
       * @param {String}
       * @returns {Void}
       */
      set(value) {
        this.accountData.industry.value = value;
        this.accountData.industry.error = '';
        this.accountData.industry.state = 'valid';
      },
    },
  },
  methods: {
    validateEmail(focus = false) {
      this.accountData.email.state = '';

      if (focus) {
        this.$refs.email.$el.querySelector('input').focus();
      }

      if (!/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(this.accountData.email.value)) {
        this.accountData.email.state = 'error';
        this.accountData.email.error = 'Not a valid email';
        return;
      }

      this.accountData.email.state = 'valid';
      this.accountData.email.error = '';
      this.$refs.didYouMean.checkEmail();
    },
    validatePassword(focus = false) {
      this.accountData.password.state = '';
      this.accountData.password.error = '';

      if (focus) {
        this.$refs.password.$el.querySelector('input').focus();
      }

      if (this.accountData.password.value.length < 8) {
        this.accountData.password.state = 'error';
        this.accountData.password.error = 'Password must be 8 characters or more';
        return;
      }

      this.accountData.password.state = 'valid';
    },
    validateName(focus = false) {
      this.accountData.name.state = '';
      this.accountData.name.error = '';

      if (focus) {
        this.$refs.name.$el.querySelector('input').focus();
      }

      if (this.accountData.name.value.length === 0) {
        this.accountData.name.state = 'error';
        this.accountData.name.error = 'Name is required';
        return;
      }

      this.accountData.name.state = 'valid';
    },
    validatePhone(focus = false) {
      this.accountData.phone.state = '';
      this.accountData.phone.error = '';

      if (focus) {
        this.$refs.phone.$el.querySelector('input').focus();
      }

      if (this.accountData.phone.value.length === 0) {
        this.accountData.phone.state = 'error';
        this.accountData.phone.error = 'Phone number is required';
        return;
      }

      this.accountData.phone.state = 'valid';
    },
    validateIndustry() {
      if (this.accountType === 'ppv') {
        if (this.accountData.industry.value === '') {
          this.accountData.industry.error = 'You need to pick an industry';
          this.accountData.industry.state = '';
          return;
        }

        this.accountData.industry.error = '';
        this.accountData.industry.state = 'valid';
      }
    },
    /**
     * Check accountname against backend to see if its available
     * @returns {Void}
     */
    async checkAccountName(focus = false) {
      this.accountData.accountName.state = '';
      this.accountData.accountName.error = '';

      if (focus === true) {
        this.$refs.accountName.$el.querySelector('input').focus();
      }

      if (this.accountData.accountName.value.length <= 2) {
        this.accountData.accountName.state = 'error';
        this.accountData.accountName.error = 'Account name is too short';
        return;
      }

      this.accountData.accountName.state = 'loading';

      const accountName = await this.$store.dispatch('AccountManager/checkAccountName', this.accountData.accountName.value);

      if (!accountName) {
        this.accountData.accountName.state = 'error';
        this.accountData.accountName.error = 'Could not check account name right now';
        return;
      }

      if (!accountName.available) {
        this.accountData.accountName.state = 'error';
        this.accountData.accountName.error = 'Account name is not available';
        return;
      }

      this.accountData.accountName.state = 'valid';
    },
    async signup() {
      const {
        accountData: {
          accountName: { value: account_name, state: accountNameState },
          email: { value: email, state: emailState },
          password: { value: password, state: passwordState },
          name: { value: name, state: nameState },
          phone: { value: phone, state: phoneState },
          industry: { value: category_id, state: industryState },
          tac: { value: tac },
        },
        accountType,
      } = this;

      if (accountNameState !== 'valid') {
        this.checkAccountName(true);
        return;
      }

      if (nameState !== 'valid') {
        this.validateName(true);
        return;
      }

      if (emailState !== 'valid') {
        this.validateEmail(true);
        return;
      }

      if (passwordState !== 'valid') {
        this.validatePassword(true);
        return;
      }

      if (phoneState !== 'valid') {
        this.validatePhone(true);
        return;
      }

      if (industryState !== 'valid') {
        this.validateIndustry(true);
        return;
      }

      if (!tac) {
        return;
      }

      this.state = 'creating';

      const payload = {
        account_name,
        email,
        password,
        name,
        category_id,
        shopping: accountType === 'liveshopping',
        ppv: accountType === 'ppv',
        phone,
      };

      const { error } = await this.$store.dispatch('User/createUser', payload);

      if (error) {
        this.state = '';

        if (error.message === 'a user with the supplied email already exists') {
          this.accountData.email.state = 'error';
          this.accountData.email.error = this.$t('errors.signup.email_exists');
        }

        if (error.message.includes('an account with fqdn')) {
          this.accountData.accountName.state = 'error';
          this.accountData.accountName.error = this.$t('errors.signup.account_name_exists');
        }

        if (error.message === 'the \'-\' character is not allowed in FQDN') {
          this.accountData.accountName.state = 'error';
          this.accountData.accountName.error = this.$t('errors.signup.dash_is_not_allowed');
        }

        if (error.message === 'FQDN can only contain letters the a-z and numbers') {
          this.accountData.accountName.state = 'error';
          this.accountData.accountName.error = this.$t('errors.signup.only_contain_letters_and_numbers');
        }

        return;
      }

      if (window.lintrk) {
        try {
          window.lintrk('track', { conversion_id: 11901962 });
        } catch (e) {
          // Do nothing, FU linkedin
        }
      }

      const token = await this.$store.dispatch('User/login', { email, password, tokenOnly: true });

      if (this.referal.source === 'app') {
        setTimeout(() => {
          try {
            window.location = `streamify://token/${token}`;
          } catch (e) { /* Nothing */ }
        }, 1000);
      } else {
        const user = await this.$store.dispatch('User/getUser');
        if (user) {
          if ([1, 6, 15, 16].indexOf(user.user.permission_id) >= 0) {
            await this.$store.dispatch('AccountManager/getAccountSettings');
            await this.$store.dispatch('AccountManager/getAccount');
            await this.$store.dispatch('AccountManager/getUsers');
            await this.$store.dispatch('AccountManager/getShoppingSettings');
          }
          this.$router.push(this.redirect ? decodeURIComponent(this.redirect) : '/dashboard');
        }
      }
    },
  },
  watch: {
    accountType(accountType) {
      if (accountType === 'liveshopping') {
        this.accountData.industry.value = 1;
        this.accountData.industry.state = 'valid';
        this.accountData.industry.error = '';
        this.accountData.phone.value = '';
        this.accountData.phone.state = '';
        this.accountData.phone.error = '';
      } else {
        this.accountData.industry.value = '';
        this.accountData.industry.state = '';
        this.accountData.industry.error = '';
        this.accountData.phone.value = '';
        this.accountData.phone.state = 'valid';
        this.accountData.phone.error = '';
      }
    },
  },
  data() {
    return {
      accountType: '',
      step: 0,
      done: false,
      state: '',
      redirect: '',
      accountData: {
        accountName: {
          value: '',
          error: '',
          state: '',
        },
        name: {
          value: '',
          error: '',
          state: '',
        },
        email: {
          value: '',
          error: '',
          state: '',
        },
        password: {
          value: '',
          error: '',
          state: '',
        },
        phone: {
          value: '',
          error: '',
          state: '',
        },
        industry: {
          value: '',
          error: '',
          state: '',
        },
        tac: {
          value: false,
          error: '',
          state: '',
          show: false,
          content: '',
        },
      },
      referal: {
        campaign: '',
        source: '',
        medium: '',
      },
    };
  },
  async mounted() {
    this.$googleanalytics.event('visited_signup', { event_category: 'user', dimension7: 'yes' });
    const referalCookie = getCookie('sf-referal') ? JSON.parse(getCookie('sf-referal')) : null;
    const referalString = getQueryParameterValue('referer');
    if (referalCookie) {
      const parentString = atob(referalCookie.content[referalCookie.content.length - 1]).split('[]');
      this.accountData.parent_account_id = parentString[0] === 'streamify' ? parseInt(parentString[1], 10) : null;
      this.referal.medium = referalCookie.medium[referalCookie.medium.length - 1];
      this.referal.source = referalCookie.source[referalCookie.source.length - 1];
      this.referal.campaign = referalCookie.campaign[referalCookie.campaign.length - 1];
    } else if (referalString !== '') {
      this.referal.source = referalString;
    }

    if (this.$route?.query?.redirect) {
      if (this.$route?.query?.redirect.indexOf('appkey') >= 0) {
        this.accountType = 'liveshopping';
        this.step = 1;
      }
    }

    if (this.$route.query.redirect) {
      this.redirect = encodeURIComponent(decodeURIComponent(this.$route.query.redirect));
    }

    const accountType = this.$route?.query?.account_type;

    if (['ppv', 'liveshopping'].includes(accountType)) {
      this.accountType = accountType;
      this.step = 1;

      this.$router.replace({ query: {} });
    }

    const { data: { success = false, content } } = await ovpFetch('https://embed.streamify.io/toc', {
      noExtraHeaders: true,
    });

    if (success) {
      this.accountData.tac.content = content;
    }

    this.$nextTick(() => {
      this.done = true;
    });
  },
};
</script>

<style lang="scss" scoped>
.signup {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: auto;
  min-height: 100vh;
  padding-top: 50px;
}

.signup-card {
  max-width: 584px;
  width: 100%;

  .card-content > div {
    padding: 24px;
  }

  h1 {
    font-weight: 600;
    font-size: 32px;
    color: #000000;
    text-align: center;
    line-height: 26px;
    margin: 20px 0 9px;
  }

  p {
    font-size: 14px;
    color: #6E6E6E;
    text-align: center;
    line-height: 21px;
  }
}

.signup-card-footer {
  border-top: 1px solid #EEF0F9;
  padding: 24px;
}

.details {
  .account-form {
    margin: 30px 10px 10px;

    label {
      margin-top: 10px;
    }

    .input-wrapper {
      margin-top: 12px;
    }
  }
}

.type {
  .account-types {
    align-items: center;
    display: flex;
    margin: 20px 10px 10px;
  }
  .account-type {
    background: #FFFFFF;
    box-shadow: 0 0 0 2px #EEF0F9;
    border-radius: 4px;
    align-items: center;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: center;
    margin: 0 10px;
    padding: 20px;
    transition: all .3s;

    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;
    }

    &:hover {
      cursor: pointer;
    }

    h2 {
      font-weight: 600;
      font-size: 16px;
      color: #000000;
      text-align: center;
      line-height: 24px;
    }

    p {
      font-size: 14px;
      color: #727479;
      text-align: center;
      line-height: 20px;
    }

    i {
      color: #C7C7C7;
      font-size: 48px;
      transition: color .3s;
    }

    &.active {
      background: #EEF5FD;
      box-shadow: 0 0 0 3px #0060FF, 0 2px 4px 0 rgba(165,179,194,0.20), 0 0 1px 0 rgba(165,179,194,0.50);
      border-radius: 4px;

      i {
        color: #0060FF;
      }
    }
  }
}

.steps {
  display: flex;
  margin: 24px 0;

  li {
    background-color: #C7C7C7;
    border-radius: 4px;
    height: 8px;
    transition: background-color .5s;
    width: 8px;
    margin: 0 4px;

    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;
    }

    &.active {
      background-color: #0060FF;
    }
  }
}

.slide-fade-enter-active {
  transition: all .3s ease;
}
.slide-fade-leave-active {
  transition: all .3s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}
.slide-fade-enter, .slide-fade-leave-to {
  transform: translateX(10px);
  opacity: 0;
}
</style>
